import { Link } from "react-router-dom";
import { useUser } from "@clerk/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Plus, Download, Lock, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { StatCards } from "../components/StatCards";
import { ShiftRow } from "../components/ShiftRow";
import { useOverview, useDeleteShift } from "../lib/queries";
import { useApi, monthKey, errMsg } from "../lib/api";

const EmptyState = () => {
  const api = useApi();
  const qc = useQueryClient();
  const sample = useMutation({
    mutationFn: () => api("post", "/shifts/sample"),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["overview"] }); toast.success(`${r.created} sample shifts added`); },
    onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <div data-testid="dashboard-empty" className="rounded-2xl border border-dashed border-border p-10 text-center">
      <h3 className="font-heading text-2xl font-extrabold uppercase">No shifts this month yet</h3>
      <p className="mt-2 text-sm text-muted-foreground">Add your first shift, or load a few sample shifts to see how it works.</p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Link to="/shifts/new" data-testid="empty-add-shift-btn" className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-5 py-2.5 text-sm font-bold text-[#0B0F17] hover:bg-amber-400">
          <Plus className="h-4 w-4" /> Add first shift
        </Link>
        <button data-testid="load-sample-shifts-btn" onClick={() => sample.mutate()} disabled={sample.isPending}
          className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-semibold hover:bg-secondary disabled:opacity-50">
          <Wand2 className="h-4 w-4" /> {sample.isPending ? "Adding…" : "Load sample shifts"}
        </button>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const { user } = useUser();
  const now = new Date();
  const { data, isLoading } = useOverview(monthKey(now));
  const del = useDeleteShift();
  const recent = data ? [...data.shifts].reverse().slice(0, 6) : [];

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-num text-xs font-bold uppercase tracking-wider text-amber-500">{format(now, "MMMM yyyy")}</p>
          <h1 className="mt-1 font-heading text-4xl font-black uppercase tracking-tight sm:text-5xl">
            Hey{user?.firstName ? `, ${user.firstName}` : ""}
          </h1>
        </div>
        <div className="flex gap-2">
          <Link to="/premium" data-testid="dashboard-export-locked-btn"
            className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2.5 text-sm font-semibold text-muted-foreground hover:text-foreground">
            <Download className="h-4 w-4" /> Export <Lock className="h-3 w-3 text-amber-500" />
          </Link>
          <Link to="/shifts/new" data-testid="dashboard-add-shift-btn"
            className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-5 py-2.5 text-sm font-bold text-[#0B0F17] transition-transform hover:-translate-y-0.5 hover:bg-amber-400">
            <Plus className="h-4 w-4" /> Add Shift
          </Link>
        </div>
      </div>

      {isLoading || !data ? (
        <div className="h-36 animate-pulse rounded-2xl bg-card" />
      ) : (
        <>
          <StatCards totals={data.totals} settings={data.settings} />
          <section>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-heading text-2xl font-extrabold uppercase tracking-tight">Recent shifts</h2>
              <Link to="/calendar" data-testid="dashboard-view-calendar" className="text-sm font-semibold text-amber-500 hover:text-amber-400">View calendar →</Link>
            </div>
            {recent.length === 0 ? <EmptyState /> : (
              <div className="space-y-3">
                {recent.map((s) => <ShiftRow key={s.id} shift={s} onDelete={(id) => del.mutate(id)} />)}
              </div>
            )}
          </section>
        </>
      )}
      <p className="text-xs text-muted-foreground">Estimated gross pay only — taxes and deductions are not included.</p>
    </div>
  );
}
